import TestimonialCard from "../components/TestimonialCard";
import StaffSettings from "../components/StaffSettings";
import PushSettings from "../components/PushSettings";
import LegalSettings, { GuideCard } from "../components/LegalSettings";
import FbPagePicker from "../components/FbPagePicker";
import SmsSettings, { smsStatus, SmsAdminNote } from "../components/SmsSettings";
import CommentReminderSettings from "../components/CommentReminderSettings";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError, API_BASE } from "../lib/api";
import { useAuth, canSimulate } from "../lib/auth";
import { useFlash } from "../lib/flash";
import { relTime } from "../lib/format";

const CHANNEL = {
  comment_reply: "Svar",
  private_reply: "Privat besked",
  post: "Opslag",
  post_edit: "Opslag rettet",
  like: "Like",
  sms: "SMS",
};

const FB_MSGS = {
  connected: ["Facebook-siden er forbundet! Nye opslag går nu på din rigtige side.", ""],
  error: ["Facebook-forbindelsen blev afbrudt eller fejlede. Prøv igen.", "error"],
  nopage: ["Vi fandt ingen Facebook-side på din konto. Opret en side og prøv igen.", "warn"],
};

export default function Settings() {
  const { refresh, store } = useAuth();
  const admin = canSimulate(store);
  const flash = useFlash();
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [fb, setFb] = useState(null);
  const [form, setForm] = useState({});
  const [pw, setPw] = useState({ current: "", password: "" });
  const [fbWaiting, setFbWaiting] = useState(false);
  const [picking, setPicking] = useState(false);

  const load = () =>
    api.get("/settings").then((r) => {
      setD(r.data);
      setForm({ ...r.data.settings });
    });
  const loadFb = () =>
    api
      .get("/facebook/status", { netGuard: false })
      .then((r) => { setFb(r.data); if (r.data.pick_pending) setPicking(true); })
      .catch(() => setFb({ error: true }));
  useEffect(() => {
    load();
    loadFb();
  }, []);
  useEffect(() => {
    if (d && window.location.hash === "#facebook") document.getElementById("facebook")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [d]);

  const connectFb = async (sw = false) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get("/facebook/oauth/start", { params: sw === true ? { switch: true } : {} });
      if (w) w.location.href = r.data.url;
      else window.open(r.data.url, "_blank", "noopener");
      setFbWaiting(`w:${fb?.connected_at || ""}`);
    } catch (err) {
      if (w) w.close();
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  useEffect(() => {
    const code = params.get("fb");
    if (!code) return;
    const m = FB_MSGS[code];
    if (m) flash(m[0], m[1]);
    if (code === "pick") setPicking(true);
    refresh();
    loadFb();
    params.delete("fb");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OAuth may finish in a separate browser (e.g. outside the installed app) – poll until connected.
  useEffect(() => {
    if (!fbWaiting) return;
    let done = false;
    const started = Date.now();
    const check = async () => {
      if (done) return;
      if (Date.now() - started > 5 * 60 * 1000) return setFbWaiting(false);
      try {
        const r = await api.get("/facebook/status");
        if (done) return;
        setFb(r.data);
        if (r.data.pick_pending) {
          done = true;
          setFbWaiting(false);
          setPicking(true);
        } else if (r.data.connected && r.data.health?.status !== "error" && `w:${r.data.connected_at || ""}` !== fbWaiting) {
          done = true;
          setFbWaiting(false);
          flash(FB_MSGS.connected[0]);
          refresh();
        }
      } catch {}
    };
    const t = setInterval(check, 3000);
    const onVis = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      done = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbWaiting]);

  if (!d) return <div className="loading">Indlæser…</div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put("/settings", {
        store_name: form.store_name,
        noshow_confirm_enabled: form.noshow_confirm_enabled === "1",
        noshow_confirm_threshold: form.noshow_confirm_threshold,
        confirm_hours: form.confirm_hours,
        noshow_block_threshold: form.noshow_block_threshold,
        reminder_before_end_hours: form.reminder_before_end_hours,
        sold_out_suffix: form.sold_out_suffix,
        sms_enabled: form.sms_enabled === "1",
        ...Object.fromEntries(["sms_ready", "sms_reminder"].map((k) => [k, form[k] !== "0"])),
        sms_extra: form.sms_extra === "1",
        sms_extra_cap: String(form.sms_extra_cap ?? ""),
        comment_reminder: form.comment_reminder === "1",
      });
      flash(r.data.flash);
      refresh();
      load();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/settings/password", pw);
      if (r.data.token) localStorage.setItem("jatak_token", r.data.token);
      flash(r.data.flash);
      setPw({ current: "", password: "" });
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Indstillinger</h1>
        </div>
      </div>
      <div className="grid cols-2">
        <form className="panel" onSubmit={saveSettings}>
          <h2>Butik og regler</h2>
          <div className="field">
            <label htmlFor="store_name">Butikkens navn</label>
            <input id="store_name" value={form.store_name || ""} onChange={set("store_name")} required data-testid="settings-name" />
          </div>
          <h3 style={{ marginTop: 18 }}>Udeblivelser</h3>
          <label className="check" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={form.noshow_confirm_enabled === "1"}
              onChange={(e) => setForm((f) => ({ ...f, noshow_confirm_enabled: e.target.checked ? "1" : "0" }))}
              data-testid="settings-noshow-confirm-toggle"
            />{" "}
            Kræv bekræftelse fra kunder, der tidligere ikke har hentet
          </label>
          <div className="row">
            {form.noshow_confirm_enabled === "1" && (
              <>
                <div className="field">
                  <label htmlFor="nct">Skal bekræfte efter</label>
                  <input id="nct" type="number" min={0} max={20} value={form.noshow_confirm_threshold || ""} onChange={set("noshow_confirm_threshold")} data-testid="settings-noshow-confirm-threshold" />
                  <div className="hint">udeblivelser</div>
                </div>
                <div className="field">
                  <label htmlFor="ch">Timer til at bekræfte</label>
                  <input id="ch" type="number" min={1} max={72} value={form.confirm_hours || ""} onChange={set("confirm_hours")} data-testid="settings-confirm-hours" />
                  <div className="hint">ellers går varen videre</div>
                </div>
              </>
            )}
            <div className="field">
              <label htmlFor="nbt">Blokeres efter</label>
              <input id="nbt" type="number" min={0} max={20} value={form.noshow_block_threshold || ""} onChange={set("noshow_block_threshold")} />
              <div className="hint">udeblivelser (0 = aldrig)</div>
            </div>
          </div>
          <SmsSettings form={form} setForm={setForm} set={set} quota={d.sms_quota} />
          <CommentReminderSettings form={form} setForm={setForm} />
          <div className="field">
            <label htmlFor="sos">Tekst øverst i opslaget ved udsolgt</label>
            <input id="sos" value={form.sold_out_suffix || ""} onChange={set("sold_out_suffix")} />
          </div>
          <button className="btn primary" data-testid="settings-save">
            Gem
          </button>
        </form>
        <div>
          <section className="panel" id="facebook">
            <h2>Facebook-side</h2>
            {picking && (
              <FbPagePicker onDone={(ok) => { setPicking(false); setFbWaiting(false); if (ok) refresh(); loadFb(); }} />
            )}
            {!fb ? (
              <p className="muted">Indlæser…</p>
            ) : fb.error ? (
              <div className="notice error" data-testid="fb-status-error">
                <p style={{ margin: "0 0 10px" }}>Kunne ikke hente Facebook-status. Tjek din forbindelse og prøv igen.</p>
                <button
                  className="btn small"
                  onClick={() => {
                    setFb(null);
                    loadFb();
                  }}
                  data-testid="fb-status-retry"
                >
                  Prøv igen
                </button>
              </div>
            ) : !fb.configured ? (
              <div className="notice warn">
                Facebook er ikke konfigureret på serveren endnu. Kontakt JaTak Pro-supporten, så hjælper vi dig i gang.
              </div>
            ) : fb.connected ? (
              <>
                {fb.health?.status === "error" ? (
                  <div className="notice error" data-testid="fb-health-notice">
                    <p style={{ margin: "0 0 6px" }}>
                      <b>Forbindelsen til {fb.page_name} er afbrudt.</b> {fb.health.reason}
                    </p>
                    <p style={{ margin: "0 0 10px" }}>Nye "ja tak"-kommentarer bliver ikke besvaret, før du forbinder siden igen.</p>
                    <button className="btn jatak" onClick={() => connectFb()} data-testid="fb-reconnect">
                      Forbind igen
                    </button>
                    {fbWaiting && <p style={{ margin: "8px 0 0", fontSize: 13 }}>Venter på godkendelse fra Facebook…</p>}
                  </div>
                ) : (
                  <div className="notice">
                    Forbundet til siden <b>{fb.page_name}</b>. Opslag går nu på din rigtige Facebook-side.
                  </div>
                )}
                <div className="actions">
                  <button
                    className="btn"
                    data-testid="fb-switch-page"
                    onClick={() => {
                      if (!window.confirm("Skift Facebook-side? Du vælger den nye side i næste trin. Aktive tilbud, der er slået op på den nuværende side, får ikke længere svar på nye kommentarer efter skiftet.")) return;
                      connectFb(true);
                    }}
                  >
                    Skift side
                  </button>
                  <button
                    className="btn danger"
                    data-testid="fb-disconnect"
                    onClick={async () => {
                      if (!window.confirm("Frakobl Facebook-siden? Nye opslag går i testtilstand igen.")) return;
                      const r = await api.post("/facebook/disconnect");
                      flash(r.data.flash);
                      refresh();
                      loadFb();
                    }}
                  >
                    Frakobl side
                  </button>
                </div>
                <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                  Skriv "ja tak" på et af dine opslag for at teste, at robotten svarer.
                </p>
              </>
            ) : (
              <>
                <p className="muted" style={{ fontSize: 14 }}>
                  Forbind din Facebook-side, så robotten svarer kunderne direkte under dine opslag.
                  {admin && " Du skal være admin/tester på appen, mens den er i udviklingstilstand."}
                </p>
                <button
                  className="btn jatak"
                  data-testid="fb-connect"
                  onClick={() => connectFb()}
                >
                  Forbind Facebook-side
                </button>
                <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                  Facebook-login åbner i en ny fane. Vend tilbage hertil, når du har godkendt.
                </p>
                {fbWaiting && !picking && (
                  <p className="notice" style={{ fontSize: 13, marginTop: 8 }} data-testid="fb-waiting">
                    Venter på godkendelse fra Facebook… Siden opdaterer automatisk.
                  </p>
                )}
              </>
            )}
            {fb && fb.configured && !fb.error && !admin && (
              <p className="muted" style={{ fontSize: 13, marginTop: 12 }} data-testid="fb-help">
                Virker det ikke? Prøv at forbinde siden igen, eller kontakt JaTak Pro-supporten.
              </p>
            )}
            {fb && fb.configured && admin && fb.webhook_url && (
              <details style={{ marginTop: 14 }} data-testid="fb-tech">
                <summary>
                  <b>Teknisk opsætning (kun admin)</b>
                </summary>
                {fb.connected && (
                  <button
                    className="btn small"
                    style={{ marginTop: 10 }}
                    data-testid="fb-register-webhook"
                    onClick={async () => {
                      try {
                        const r = await api.post("/facebook/register-webhook");
                        flash(r.data.flash);
                      } catch (err) {
                        flash(apiError(err.response?.data?.detail) || err.message, "error");
                      }
                    }}
                  >
                    Gentilmeld webhook
                  </button>
                )}
                <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                  I Meta-appen under <b>Webhooks → Page</b>:
                </p>
                <div className="field">
                  <label>Callback-URL</label>
                  <input readOnly value={fb.webhook_url} onFocus={(e) => e.target.select()} data-testid="fb-webhook-url" />
                </div>
                <div className="field">
                  <label>Verify token</label>
                  <input readOnly value={fb.verify_token} onFocus={(e) => e.target.select()} data-testid="fb-verify-token" />
                </div>
                <div className="field">
                  <label>OAuth redirect URI (Facebook Login → Indstillinger)</label>
                  <input readOnly value={fb.redirect_uri} onFocus={(e) => e.target.select()} data-testid="fb-redirect-uri" />
                </div>
                {fb.meta_urls && (
                  <div data-testid="fb-meta-urls">
                    <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                      I Meta App Dashboard under <b>App-indstillinger → Grundlæggende</b> og <b>Facebook Login → Indstillinger</b>:
                    </p>
                    {Object.entries(fb.meta_urls).map(([label, url], i) => (
                      <div className="field" key={label}>
                        <label>{label}</label>
                        <input readOnly value={url} onFocus={(e) => e.target.select()} data-testid={`fb-meta-url-${i}`} />
                      </div>
                    ))}
                  </div>
                )}
                <p className="muted" style={{ fontSize: 13 }}>
                  Abonnér på felterne <b>feed</b> og <b>messages</b>. Siden abonneres automatisk, når du forbinder den
                  ovenfor.
                </p>
              </details>
            )}
            <p className="muted" style={{ fontSize: 13, margin: "12px 0 0" }} data-testid="sms-status-line">
              SMS-påmindelser: {smsStatus(form)}.
            </p>
            <SmsAdminNote info={d.sms_platform} />
          </section>
          <form className="panel" onSubmit={savePassword}>
            <h2>Konto</h2>
            <p className="muted">
              Logget ind som <b>{d.email}</b>
            </p>
            <div className="field">
              <label htmlFor="cur">Nuværende adgangskode</label>
              <input id="cur" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required />
            </div>
            <div className="field">
              <label htmlFor="npw">Ny adgangskode</label>
              <input id="npw" type="password" minLength={8} value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} required />
            </div>
            <button className="btn">Skift adgangskode</button>
          </form>
          <PushSettings />
          <LegalSettings />
          <GuideCard />
          <StaffSettings />
          <TestimonialCard />
          <section className="panel" id="log">
            <h2>Udgående beskeder</h2>
            {d.outbox.length ? (
              <ul className="feed">
                {d.outbox.map((l) => (
                  <li key={l.id}>
                    <span>
                      <b>{CHANNEL[l.channel] || l.channel}</b>
                      {l.ok ? "" : <span className="pill no_show"> fejl</span>} {(l.text || "").slice(0, 140)}
                      {!l.ok && l.error && (
                        <small style={{ display: "block", color: "var(--danger, #c0392b)", marginTop: 4, wordBreak: "break-word" }} data-testid={`outbox-error-${l.id}`}>
                          {String(l.error).slice(0, 600)}
                        </small>
                      )}
                    </span>
                    <time>{relTime(l.created_at)}</time>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Intet sendt endnu.</p>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
